import { resolveFieldLabel } from "@repo/shared";
import type { LocalizedString } from "@repo/shared/types";
import {
  type CapacityInputFrequency,
  type CapacityMultiplierBasis,
  type FinancialFieldDefinition,
  type FinancialSituationModelSettings,
  type FsClientType,
  fieldAppliesTo,
  type InvestmentCapacityVariantConfig,
} from "@repo/shared/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ApiError } from "@/lib/api/_client";
import { useEditFields } from "@/lib/hooks/use-clients";
import { useUpdateFinancialSituationModel } from "@/lib/hooks/use-financial-situation-models";
import { useUserLanguage } from "@/lib/i18n";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./model-settings-editor.i18n";

/** QP-183/QL-140 — the settings key holding each client type's capacity config. */
type VariantKey = "person" | "company" | "group";

const VARIANT_KEY_BY_CLIENT_TYPE: Record<FsClientType, VariantKey> = {
  Person: "person",
  Company: "company",
  Group: "group",
};

const CLIENT_TYPE_BY_VARIANT_KEY: Record<VariantKey, FsClientType> = {
  person: "Person",
  company: "Company",
  group: "Group",
};

/**
 * QL-107 — the slice of a client field the fixed-buffer picker reads.
 * `useEditFields` is untyped (`any[]`), so the shape is named here;
 * `nameI18n` mirrors what `getLocalizedValue` accepts.
 */
type ClientFieldOption = {
  id: string;
  type?: string;
  name?: string;
  nameI18n?: string | LocalizedString | null;
};

const EMPTY_VARIANT: InvestmentCapacityVariantConfig = {
  enabled: false,
  bufferFloor: 0,
  bufferMultiplier: 0,
  shortTermExpensesEnabled: false,
  assetFieldIds: [],
  fixedBufferClientField: null,
  fixedBufferAmount: null,
};

/**
 * QL-143 — settings are a sparse bag of "off by default" values: an absent key,
 * `false`, `0` and `[]` all mean the same thing (see EMPTY_VARIANT). A raw
 * JSON compare would therefore call the form dirty after a switch is toggled on
 * and straight back off, because the key now exists where it did not before.
 * Canonicalise first: drop every default-valued entry, sort the rest, and the
 * comparison reflects meaning instead of object shape.
 */
const canonicalSettings = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.length ? value : undefined;
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .map(([key, val]) => [key, canonicalSettings(val)] as const)
      .filter(([, val]) => val !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
    return entries.length ? Object.fromEntries(entries) : undefined;
  }
  if (value === null || value === false || value === 0) return undefined;
  return value;
};

type ModelSettingsEditorProps = {
  modelId: string;
  settings: FinancialSituationModelSettings;
  /** SAVED asset fields — the selectable pool for `assetFieldIds`. */
  assetFields: FinancialFieldDefinition[];
  /**
   * QP-176/QL-140 — the page-level Persons/Companies/Groups view switch; the
   * capacity section shows only the matching variant.
   */
  activeClientType: FsClientType;
};

export function ModelSettingsEditor({
  modelId,
  settings,
  assetFields,
  activeClientType,
}: ModelSettingsEditorProps) {
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const updateModel = useUpdateFinancialSituationModel();

  // QL-107 — the fixed buffer trigger pool. /edit returns every active field,
  // so booleans hidden from the client list still qualify; the endpoint mixes
  // in standard fields under synthetic "standard-*" ids, whose values live
  // outside the custom-field value store and are therefore not eligible.
  const { editFields, isLoading: fieldsLoading, error: fieldsError } = useEditFields();
  const booleanClientFields = useMemo<ClientFieldOption[]>(
    () =>
      editFields.filter(
        (field: ClientFieldOption) => !field.id.startsWith("standard-") && field.type === "boolean",
      ),
    [editFields],
  );
  const clientFieldLabel = useCallback(
    (field: ClientFieldOption) => localize(field.nameI18n) || field.name || "",
    [userLanguage],
  );
  // QL-107 — the pool is empty both before it loads and when the request fails,
  // so a saved reference must not be judged stale until it has actually arrived.
  const fieldPoolReady = !fieldsLoading && !fieldsError;

  const [draft, setDraft] = useState<FinancialSituationModelSettings>(settings);

  useEffect(() => {
    setDraft(settings);
  }, [settings]);

  // QL-143 — same rule as the field-definitions editor above it on this page:
  // the draft starts as a copy of the saved settings, so Save waits for a real
  // change instead of sitting armed with nothing to send.
  const isDirty = useMemo(
    () => JSON.stringify(canonicalSettings(draft)) !== JSON.stringify(canonicalSettings(settings)),
    [draft, settings],
  );

  const variant = (key: VariantKey): InvestmentCapacityVariantConfig =>
    draft.investmentCapacity?.[key] ?? EMPTY_VARIANT;

  const setVariant = (key: VariantKey, updates: Partial<InvestmentCapacityVariantConfig>) => {
    setDraft((prev) => ({
      ...prev,
      investmentCapacity: {
        ...prev.investmentCapacity,
        [key]: { ...(prev.investmentCapacity?.[key] ?? EMPTY_VARIANT), ...updates },
      },
    }));
  };

  const handleSave = async () => {
    for (const key of ["person", "company", "group"] as const) {
      const v = draft.investmentCapacity?.[key];
      if (v?.enabled && v.assetFieldIds.length === 0) {
        toast.error(t.error, { description: t.assetFieldsRequired });
        return;
      }
      // QL-107 — mirrors the server rule; a chosen field with no usable amount
      // is a rule that can never do anything.
      if (v?.fixedBufferClientField && !(Number(v.fixedBufferAmount) > 0)) {
        toast.error(t.error, { description: t.fixedBufferAmountRequired });
        return;
      }
    }
    try {
      await updateModel.mutateAsync({ id: modelId, data: { settings: draft } });
      toast.success(t.saved);
    } catch (err) {
      toast.error(t.error, {
        description: err instanceof ApiError ? err.message : t.failedToSave,
      });
    }
  };

  const parseNonNegative = (raw: string): number => {
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  };

  // QL-107 — unlike the buffer floor/multiplier, an empty box means "no amount"
  // rather than 0, so it must survive as null. 0 itself is kept as typed and
  // rejected on save (both here and server-side) instead of vanishing mid-edit.
  const parseAmountOrNull = (raw: string): number | null => {
    if (raw.trim() === "") return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  };

  const switchRow = (
    label: string,
    help: string,
    checked: boolean,
    onChange: (v: boolean) => void,
  ) => (
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-0.5">
        <Label className="text-sm">{label}</Label>
        <p className="text-xs text-muted-foreground">{help}</p>
      </div>
      <Switch aria-label={t.enabled} checked={checked} onCheckedChange={onChange} />
    </div>
  );

  // QP-176/QL-140 — the variant follows the page-level client-type switch; its
  // Enabled toggle sits on the section heading line (no per-variant sub-box).
  const activeVariantKey = VARIANT_KEY_BY_CLIENT_TYPE[activeClientType];

  const renderVariantConfig = (key: VariantKey) => {
    const v = variant(key);
    const clientType = CLIENT_TYPE_BY_VARIANT_KEY[key];
    const applicableAssetFields = assetFields.filter((f) => fieldAppliesTo(f, clientType));
    // QL-140 — for groups the floor is an amount PER MEMBER; the engine
    // multiplies it by the number of member rows (see computeCapacity).
    const isGroup = key === "group";
    // QL-107 — a saved reference the picker can no longer offer: the field was
    // deleted, deactivated, or retyped. Nothing clears it, so the rule silently
    // stops applying and the buffer falls back to the (usually lower) derived
    // minimum. Read off `draft`, so choosing a field or clearing the setting
    // resolves the warning without any extra state.
    const staleFixedBuffer =
      !!v.fixedBufferClientField &&
      fieldPoolReady &&
      !booleanClientFields.some((f) => f.id === v.fixedBufferClientField);
    return (
      <div className="space-y-4">
        {v.enabled && (
          <>
            <div className="space-y-4">
              {/* QL-127 — both modes default to the pre-QL-127 behaviour, so an
                  absent key is shown as its default rather than written out. */}
              <div className="space-y-1">
                <Label className="text-xs">{t.inputFrequency}</Label>
                <Select
                  value={v.inputFrequency ?? "monthly"}
                  onValueChange={(value) =>
                    setVariant(key, { inputFrequency: value as CapacityInputFrequency })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">{t.inputFrequencyMonthly}</SelectItem>
                    <SelectItem value="annual">{t.inputFrequencyAnnual}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t.inputFrequencyHelp}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t.multiplierBasis}</Label>
                <Select
                  value={v.bufferMultiplierBasis ?? "income"}
                  onValueChange={(value) =>
                    setVariant(key, { bufferMultiplierBasis: value as CapacityMultiplierBasis })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">{t.multiplierBasisIncome}</SelectItem>
                    <SelectItem value="expenses">{t.multiplierBasisExpenses}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t.multiplierBasisHelp}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">{t.bufferMultiplier}</Label>
                <Input
                  type="number"
                  min={0}
                  value={v.bufferMultiplier}
                  onChange={(e) =>
                    setVariant(key, { bufferMultiplier: parseNonNegative(e.target.value) })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  {v.bufferMultiplierBasis === "expenses"
                    ? t.bufferMultiplierHelpExpenses
                    : t.bufferMultiplierHelp}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">{isGroup ? t.bufferFloorGroup : t.bufferFloor}</Label>
                <Input
                  type="number"
                  min={0}
                  value={v.bufferFloor}
                  onChange={(e) =>
                    setVariant(key, { bufferFloor: parseNonNegative(e.target.value) })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  {isGroup ? t.bufferFloorHelpGroup : t.bufferFloorHelp}
                </p>
              </div>
            </div>

            {/* QL-107 — fixed buffer. The chosen field IS the enabled state, so
                there is no separate switch; clearing it clears the amount. */}
            <div className="space-y-1">
              <Label className="text-xs">{t.fixedBuffer}</Label>
              <Select
                value={v.fixedBufferClientField ?? "none"}
                onValueChange={(value) =>
                  setVariant(
                    key,
                    value === "none"
                      ? { fixedBufferClientField: null, fixedBufferAmount: null }
                      : { fixedBufferClientField: value },
                  )
                }
              >
                <SelectTrigger className={cn(staleFixedBuffer && "border-warning text-warning")}>
                  {/* A stale id matches no item, so the trigger would otherwise
                      fall back to the placeholder and read "No fixed buffer". */}
                  {staleFixedBuffer ? (
                    <SelectValue>{t.fixedBufferStaleSelection}</SelectValue>
                  ) : (
                    <SelectValue placeholder={t.fixedBufferNone} />
                  )}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t.fixedBufferNone}</SelectItem>
                  {booleanClientFields.map((field) => (
                    <SelectItem key={field.id} value={field.id}>
                      {clientFieldLabel(field)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p
                className={cn(
                  "text-xs",
                  staleFixedBuffer ? "text-warning" : "text-muted-foreground",
                )}
              >
                {staleFixedBuffer
                  ? t.fixedBufferStale
                  : booleanClientFields.length === 0 && !v.fixedBufferClientField
                    ? t.noBooleanClientFields
                    : t.fixedBufferHelp}
              </p>
            </div>

            {v.fixedBufferClientField && (
              <div className="space-y-1">
                <Label className="text-xs">{t.fixedBufferAmount}</Label>
                <Input
                  type="number"
                  min={0}
                  value={v.fixedBufferAmount ?? ""}
                  onChange={(e) =>
                    setVariant(key, { fixedBufferAmount: parseAmountOrNull(e.target.value) })
                  }
                />
              </div>
            )}

            {switchRow(
              t.shortTermExpenses,
              t.shortTermExpensesHelp,
              v.shortTermExpensesEnabled === true,
              (checked) => setVariant(key, { shortTermExpensesEnabled: checked }),
            )}

            <div className="space-y-2">
              <Label className="text-sm">{t.assetFields}</Label>
              <p className="text-xs text-muted-foreground">{t.assetFieldsHelp}</p>
              {applicableAssetFields.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t.noAssetFields}</p>
              ) : (
                <div className="space-y-1">
                  {applicableAssetFields.map((field) => (
                    <div key={field.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        id={`capacity-${key}-${field.id}`}
                        checked={v.assetFieldIds.includes(field.id)}
                        onCheckedChange={(checked) =>
                          setVariant(key, {
                            assetFieldIds:
                              checked === true
                                ? [...v.assetFieldIds, field.id]
                                : v.assetFieldIds.filter((fid) => fid !== field.id),
                          })
                        }
                      />
                      <Label htmlFor={`capacity-${key}-${field.id}`} className="font-normal">
                        {resolveFieldLabel(field, userLanguage) || field.id}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.title}</CardTitle>
        <CardDescription>{t.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {switchRow(
          t.oneFieldInAssetsRequired,
          t.oneFieldInAssetsRequiredHelp,
          draft.oneFieldInAssetsRequired === true,
          (checked) => setDraft((prev) => ({ ...prev, oneFieldInAssetsRequired: checked })),
        )}
        {switchRow(
          t.oneFieldInLiabilitiesRequired,
          t.oneFieldInLiabilitiesRequiredHelp,
          draft.oneFieldInLiabilitiesRequired === true,
          (checked) => setDraft((prev) => ({ ...prev, oneFieldInLiabilitiesRequired: checked })),
        )}
        {switchRow(
          t.expectingChanges,
          t.expectingChangesHelp,
          draft.expectingChangesQuestion?.enabled === true,
          (checked) =>
            setDraft((prev) => ({ ...prev, expectingChangesQuestion: { enabled: checked } })),
        )}

        <div className="border-t pt-4 space-y-4">
          {/* QP-176 — heading + the active variant's Enabled switch on one line. */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="text-base font-medium">{t.capacityHeading}</h4>
              <p className="text-sm text-muted-foreground">{t.capacityHelp}</p>
            </div>
            <div className="flex items-center shrink-0">
              <Switch
                aria-label={t.enabled}
                checked={variant(activeVariantKey).enabled}
                onCheckedChange={(checked) => setVariant(activeVariantKey, { enabled: checked })}
              />
            </div>
          </div>

          {renderVariantConfig(activeVariantKey)}

          {switchRow(
            t.validateDeposits,
            t.validateDepositsHelp,
            draft.investmentCapacity?.validateDeposits === true,
            (checked) =>
              setDraft((prev) => ({
                ...prev,
                investmentCapacity: { ...prev.investmentCapacity, validateDeposits: checked },
              })),
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={handleSave} loading={updateModel.isPending} disabled={!isDirty}>
            {t.save}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
